import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotificationBell({ token }) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();
    // Check for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications(true);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async (silent = false) => {
    try {
      // First check and create new notifications
      const checkRes = await axios.post(`${API}/notifications/check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then fetch all notifications
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newNotifs = response.data;
      
      // Show push toast for new unread notifications
      if (!silent) {
        // Initial load - no toasts
      } else {
        const oldIds = notifications.map(n => n.id);
        const brandNew = newNotifs.filter(n => !oldIds.includes(n.id) && n.status === 'sent');
        brandNew.forEach(n => {
          // Browser push notification
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('SHALEM', { body: n.message, icon: '/icons/icon-192x192.png' });
          }
        });
      }
      
      setNotifications(newNotifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => n.status === 'sent').length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={`relative p-2 rounded-full hover:bg-slate-100 ${unreadCount > 0 ? 'animate-pulse-slow' : ''}`}
          data-testid="notification-bell"
        >
          <Bell className={`h-6 w-6 ${unreadCount > 0 ? 'text-blue-600' : 'text-slate-700'}`} />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-600 text-white text-xs"
              data-testid="notification-badge"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notification-popover">
        <div className="p-4 border-b">
          <h3 className="font-bold text-lg">{t('notifications')}</h3>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length > 0 ? (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-slate-50 transition-colors ${
                    notification.status === 'sent' ? 'bg-blue-50' : ''
                  }`}
                  data-testid="notification-item"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">
                        {t('bill_reminder')}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(notification.sent_date).toLocaleDateString()}
                      </p>
                    </div>
                    {notification.status === 'sent' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markAsRead(notification.id)}
                        className="text-xs"
                        data-testid="mark-read-button"
                      >
                        {t('mark_as_read')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500" data-testid="no-notifications">
              <Bell className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>{t('no_notifications')}</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
